import { Injectable, NgZone } from "@angular/core";
import { createEffect, Actions, ofType } from "@ngrx/effects";
import { EMPTY, firstValueFrom, from, Observable, of } from "rxjs";
import * as GameActions  from "./game.actions";
import { GameService } from "../../firebase/game.service";
import { switchMap, tap, withLatestFrom, map } from "rxjs/operators";
import { AppState } from "../app.state";
import { Store } from "@ngrx/store";
import { NavigationExtras, Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import * as GameHistoryActions from '../gameHistory/gameHistory.actions';
import * as AuthActions from '../auth/auth.actions';
import { GameSocketStoreDispatcher } from "../gameSocket/gameSocket.dispatcher";
import { ComponentToLoad, GameToLoad, StreamToLoad } from "./game.state";
import { SopoTransitionService } from "src/app/shared/services/sopo-transition/sopo-transition.service";
import { CurrencyType } from "@soposopo/sopo-models";
import { SplitScreenService } from "src/app/game/splitScreen.service";
import { collection, collectionData, Firestore, query } from "@angular/fire/firestore";
import { isElectron } from "src/app/shared/utils/platform.utils";
import { GameTabManager } from "src/app/shared/services/game-tab-manager/game-tab-manager.interface";

@Injectable()
export class GameEffects {
  constructor(
    private actions$: Actions,
    private gameService: GameService,
    private store$: Store<AppState>,
    private navCtrl: NavController,
    private router: Router,
    private gameSocketDispatcher: GameSocketStoreDispatcher,
    private sopoTransitionService: SopoTransitionService,
    private splitScreenService: SplitScreenService,
    private zone: NgZone,
    private firestore: Firestore,
    private gameTabManager: GameTabManager,
  ) {}


  loadGameAction$ = createEffect(() => this.actions$.pipe(
    ofType(GameActions.leaveTableAction),
    switchMap((payload) => {
      return this.gameService
        .leaveTable()
        .then((snapshot) => {
          return GameActions.leaveTableCompletedAction({gameStateId: payload.gameStateId});
        })
        .catch((error) => {
          return GameActions.gameErrorAction({error,gameStateId: payload.gameStateId});
        });
    })
  ));


  addNewGameAction$ = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.addNewGameToState),
    withLatestFrom(this.store$),
    tap(async ([action, state]) => {
      if (action.toNav) {
        //nav
        const navigationExtras: NavigationExtras = {
          replaceUrl: true,
          state: {
            promptOwner: action.promptOwner
          }
        }
        if (action.game.componentType === 'game') {
          let game: GameToLoad = {
            componentType: "game",
            gameStateId: action.game.gameStateId,
            tableId: action.game.tableId,
            tournamentId: action.game.tournamentId,
            type: action.game.type,
            clubId: action.game.clubId,
          } 
        this.saveTabGameToLocalStorage(game);
        this.saveGameListToLocalStorage([game]);
        }
      }
    }),
    switchMap(([action, state])  => {
        if (action.game.componentType === 'game') return of(GameHistoryActions.addNewHistoryAction({gameStateId: action.game.gameStateId}));
        else return EMPTY
    })
  ));

  removeGameAction$ = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.removeGameFromGamesList),
    withLatestFrom(this.store$),
    switchMap(([action, state]) => {
      const updatedGamesList = state.game.gamesList.filter(
        (g) => g.gameStateId !== action.gameStateId
      );
      this.saveGameListToLocalStorage(updatedGamesList);
        if (action.removedGame) {
            if (action.removedGame.componentType === 'clubChat') localStorage.removeItem('selectedClubChat');
            if (action.removedGame.componentType === 'game') {
                this.gameSocketDispatcher.getUnfilteredGameSocketAsPromise(action.gameStateId).then(
                    async (socket) => {
                      if (!socket) return;
                      await socket.leaveRoom((action.removedGame as GameToLoad).tableId);
                      this.gameSocketDispatcher.removeSocket((action.removedGame as GameToLoad).gameStateId);
                    }
                );
            }
        }
        if (state.game.gamesList.length != 0) {
            let clubGameExists = false;
            let clubChatIsOpen = false;
            let freeplayIsOpen = false;
            for (let component of state.game.gamesList) {
                if (component.componentType === 'clubChat') clubChatIsOpen = true;
                if (component.componentType === 'game' && component.clubId !== 'SOPO') clubGameExists = true;
                if (component.componentType === 'game' && component.clubId === 'SOPO') freeplayIsOpen = true;
            }
            if (clubChatIsOpen && freeplayIsOpen && !clubGameExists) {
                this.splitScreenService.closeGame('clubChat');
            }
        }
        if (state.game.gamesList.length == 0) {
            let removedGameClubId;
            if(action.removedGame && action.removedGame.componentType === 'game') removedGameClubId = action.removedGame.clubId;
            const clubId = removedGameClubId || state.club.id;
            if (this.router.url.includes('game')) {
                this.sopoTransitionService.openTransition().then(() => {
                    const tournamentType = state.game.games[action.gameStateId]?.tournament?.tournamentType;
                    if (clubId && clubId !== 'SOPO') {
                        this.zone.run(async () => {
                            const userId = state.auth.userID;
                            const collectionReference = collection(this.firestore, `users/${userId}/clubs`);
                            const collectionQuery = query(collectionReference);
                            const userClubs = await firstValueFrom(collectionData(collectionQuery));
                            const clubFound = userClubs.find( club => club.id === clubId) != undefined;
                            let navUrl = `club-lobby/${clubId}`;
                            let navOptions: NavigationExtras = {};
                            if(!clubFound) {
                              navUrl = 'lobby/home/club';
                              navOptions = { queryParams: { openClubList: true, bypassResumeGuard: true }};
                            }
                            this.navCtrl.navigateBack(navUrl, navOptions);
                        });
                    }
                    else {
                        const navigationExtras: NavigationExtras = {
                            queryParams: {
                                bypassResumeGuard: true
                            },
                            replaceUrl: true,
                        }

                        let url: string;
                        let gameStateId = action.gameStateId;

                        switch (action.currency) {
                            // case CurrencyType.SWEEPBUCK:
                            //     url = 'lobby/home/sweepstakes';
                            //     break;
                            default:
                                url = 'lobby';
                        }
                        if (action.removedGame?.componentType === 'stream') {
                            gameStateId = (action.removedGame as StreamToLoad).gameToPreviewId;
                        }

                        if (state.game.games[gameStateId]?.table?.isQuickplay && tournamentType === "MTT" && !action.invitationRejected) {
                            url = `tournament-registration/SOPO/${state.game.games[gameStateId].tournament.id}`;
                        }
                        this.zone.run(() => {
                            this.navCtrl.navigateBack(url, navigationExtras);
                        });
                    }
                    if(isElectron()) {
                        let gameTabName: string;
                        if(tournamentType === "MTT" || tournamentType === "SNG") {
                            gameTabName = `gameTab-${state.game.games[action.gameStateId]?.tournament.id}-${action.gameStateId}`;
                        } else {
                            gameTabName = `gameTab-${action.gameStateId}`;
                        }
                        this.gameTabManager.close(gameTabName);
                    } else {
                        const channel = new BroadcastChannel('gameTab-manager');
                        if(tournamentType === "MTT" || tournamentType === "SNG") {
                            channel.postMessage({
                                type: 'CLOSE_TABLE',
                                gameTabName: `gameTab-${state.game.games[action.gameStateId]?.tournament.id}-${action.gameStateId}`,
                            });
                        } else {
                            channel.postMessage({
                                type: 'CLOSE_TABLE',
                                gameTabName: `gameTab-${action.gameStateId}`,
                            });
                        }
                        channel.close();
                    }
                });
            }
            return of(GameActions.GameDispatcherActions.removeAllGameStates());
        }
        return EMPTY;
    })
  ));

  modifyGameSettingsAction$: Observable<void> = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.modifyGameSettings),
    withLatestFrom(this.store$),
    map(([action, state]) => {
      this.saveGameListToSessionStorage(state.game.gamesList);
    })
  ), { dispatch: false });

  resetGamesAction$ = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.removeAllGameStates),
    switchMap(action => {
        return of(GameHistoryActions.clearUnusedHistoriesAction({remainingGames: action.remainingGames}));
    })
  ));


  clearGamesListAction$: Observable<void> = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.clearGamesList),
    withLatestFrom(this.store$),
    map(([_, state]) => {
      this.saveGameListToSessionStorage(state.game.gamesList);
    })
  ), {dispatch: false});

  closeClubGamesAction$: Observable<void> = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.closeAllClubGames),
    withLatestFrom(this.store$),
    map(([action, state]) => {
        const gamesList = state.game.gamesList;
        gamesList.filter(game => game.componentType === 'game').forEach((game: GameToLoad) => {
            if (action.clubId === game.clubId) this.store$.dispatch(GameActions.GameDispatcherActions.removeGameFromGamesList({gameStateId: game.gameStateId}));
        })
    })
  ), {dispatch: false});

  selectGameAction$: Observable<void> = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.changeSelectedGame),
    withLatestFrom(this.store$),
    map(([action, state]) => {
        const gameToSelect = state.game.gamesList.find(game => game.gameStateId == action.gameStateId);
        if (gameToSelect) {
            sessionStorage.setItem('selectedGame', gameToSelect.gameStateId);
        }
    })
  ), { dispatch: false });


  SetGridViewAction$: Observable<void> = createEffect(() => this.actions$.pipe(
    ofType(GameActions.GameDispatcherActions.setGridView),
    withLatestFrom(this.store$),
    map(([action, state]) => {
        sessionStorage.setItem('gridView', `${action.enabled}`);
    })
  ), { dispatch: false });


    logoutAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.logoutAction),
        withLatestFrom(this.store$),
        switchMap(([_, state]) => state.game.gamesList.map(game => GameActions.GameDispatcherActions.removeGameFromGamesList({gameStateId: game.gameStateId}))),
    ));

    clubIdChanged$ = createEffect(() => this.actions$.pipe(
        ofType(GameActions.GameDispatcherActions.changeClubId),
        withLatestFrom(this.store$),
        map(([_, state]) => {
          this.saveGameListToSessionStorage(state.game.gamesList);
        })
    ), {dispatch: false});

    updateGamePreviewIdAction$ = createEffect(() => this.actions$.pipe(
      ofType(GameActions.GameDispatcherActions.updateGameToPreviewId),
      withLatestFrom(this.store$),
      map(([_, state]) => {
        this.saveGameListToSessionStorage(state.game.gamesList);
      })
    ), {dispatch: false})

    private saveGameListToSessionStorage(gamesList: ComponentToLoad[]) {
      sessionStorage.setItem('gamesToLoad', JSON.stringify(gamesList));
    }
    
    private saveTabGameToLocalStorage(game: GameToLoad) {
      var now = new Date().getTime();
      if (
        localStorage.getItem("setupTime") !== null &&
        now - Number(localStorage.getItem("setupTime")) > 30 * 60 * 1000
      ) {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("gameInfo-")) {
            localStorage.removeItem(key);
          }
        });
      }
      localStorage.setItem("setupTime", now.toString());
      localStorage.setItem(
        `gameInfo-${game.gameStateId}`,
        JSON.stringify({
          componentType: "game",
          gameStateId: game.gameStateId,
          tableId: game.tableId,
          tournamentId: game.tournamentId,
          type: game.type,
          clubId: game.clubId,
        })
      );
    }

    private saveGameListToLocalStorage(games: ComponentToLoad[]) {
      let gamesToStore = JSON.parse(localStorage.getItem("gamesToLoad") || "[]");
      const lastGame = games[games.length - 1];
      if(lastGame) {
        gamesToStore = [lastGame];
      }
      localStorage.setItem("gamesToLoad", JSON.stringify(gamesToStore));
    }
}
